import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Paper,
  Fab,
  Badge,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Psychology as AIIcon,
  Chat as ChatIcon,
  Search as SearchIcon,
  Calculate as CalcIcon,
  Dashboard as DashboardIcon,
  Analytics as AnalyticsIcon
} from '@mui/icons-material';

// Import des composants
import Dashboard from './Dashboard';
import AnalyticsSystem from './AnalyticsSystem';
import ChatBot from './ChatBot';
import SmartMatching from './SmartMatching';
import CostPredictor from './CostPredictor';

// Interface d'onglets
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`ai-tabpanel-${index}`}
      aria-labelledby={`ai-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const AppWithAI = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [chatBotOpen, setChatBotOpen] = useState(false);
  const [smartMatchingOpen, setSmartMatchingOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadMessages, setUnreadMessages] = useState(0);

  // Gestion des événements globaux pour le chatbot
  useEffect(() => {
    const handleOpenChatBot = () => {
      setChatBotOpen(true);
    };

    const handleAINotification = (event) => {
      const notification = event.detail;
      setNotifications(prev => [...prev, {
        id: Date.now(),
        message: notification.message,
        type: notification.type || 'info',
        timestamp: new Date()
      }]);
    };

    window.addEventListener('openChatBot', handleOpenChatBot);
    window.addEventListener('aiNotification', handleAINotification);

    return () => {
      window.removeEventListener('openChatBot', handleOpenChatBot);
      window.removeEventListener('aiNotification', handleAINotification);
    };
  }, []);

  // Simulation de messages non lus
  useEffect(() => {
    const interval = setInterval(() => {
      if (!chatBotOpen && Math.random() > 0.8) {
        setUnreadMessages(prev => prev + 1);
      }
    }, 30000); // Chaque 30 secondes

    return () => clearInterval(interval);
  }, [chatBotOpen]);

  // Réinitialiser les messages non lus quand le chat s'ouvre
  useEffect(() => {
    if (chatBotOpen) {
      setUnreadMessages(0);
    }
  }, [chatBotOpen]);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleChatBotToggle = () => {
    setChatBotOpen(!chatBotOpen);
  };

  const handleChatBotClose = () => {
    setChatBotOpen(false);
  };

  const handleSmartMatchingOpen = () => {
    setSmartMatchingOpen(true);
  };

  const handleSmartMatchingClose = () => {
    setSmartMatchingOpen(false);
  };

  const dismissNotification = (notificationId) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  };

  // Configuration des onglets
  const tabs = [
    {
      label: "Tableau de Bord",
      icon: <DashboardIcon />,
      component: <Dashboard />
    },
    {
      label: "Analytics & Rapports",
      icon: <AnalyticsIcon />,
      component: <AnalyticsSystem />
    },
    {
      label: "Prédicteur de Coûts IA",
      icon: <CalcIcon />,
      component: <CostPredictor />
    }
  ];

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Header avec navigation */}
      <Paper sx={{ borderRadius: 0, mb: 0 }}>
        <Container maxWidth="xl">
          <Box sx={{ py: 2 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              <AIIcon sx={{ mr: 2, verticalAlign: 'middle', color: 'primary.main' }} />
              SaaS Artisans - Plateforme IA
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Votre assistant intelligent pour la gestion d'artisans
            </Typography>
          </Box>
          
          {/* Navigation par onglets */}
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderTop: 1, borderColor: 'divider' }}
          >
            {tabs.map((tab, index) => (
              <Tab
                key={index}
                label={tab.label}
                icon={tab.icon}
                iconPosition="start"
                sx={{ minHeight: 64 }}
              />
            ))}
          </Tabs>
        </Container>
      </Paper>

      {/* Contenu principal */}
      <Container maxWidth="xl">
        {tabs.map((tab, index) => (
          <TabPanel key={index} value={currentTab} index={index}>
            {tab.component}
          </TabPanel>
        ))}
      </Container>

      {/* Boutons d'action flottants */}
      <Box sx={{ position: 'fixed', bottom: 20, right: 20, zIndex: 1000 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Bouton Smart Matching */}
          <Fab
            color="secondary"
            onClick={handleSmartMatchingOpen}
            sx={{
              background: 'linear-gradient(45deg, #FF6B6B 30%, #FF8E8E 90%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #FF5252 30%, #FF6B6B 90%)',
              }
            }}
          >
            <SearchIcon />
          </Fab>

          {/* Bouton ChatBot avec badge */}
          <Badge badgeContent={unreadMessages} color="error">
            <Fab
              color="primary"
              onClick={handleChatBotToggle}
              sx={{
                background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #1976D2 30%, #2196F3 90%)',
                }
              }}
            >
              <ChatIcon />
            </Fab>
          </Badge>
        </Box>
      </Box>

      {/* ChatBot */}
      <ChatBot
        isOpen={chatBotOpen}
        onToggle={handleChatBotToggle}
        onClose={handleChatBotClose}
      />

      {/* Smart Matching */}
      <SmartMatching
        isOpen={smartMatchingOpen}
        onClose={handleSmartMatchingClose}
      />

      {/* Notifications IA */}
      {notifications.map((notification) => (
        <Snackbar
          key={notification.id}
          open={true}
          autoHideDuration={6000}
          onClose={() => dismissNotification(notification.id)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            onClose={() => dismissNotification(notification.id)}
            severity={notification.type}
            variant="filled"
            sx={{ width: '100%' }}
          >
            <Typography variant="body2">
              <AIIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
              {notification.message}
            </Typography>
          </Alert>
        </Snackbar>
      ))}

      {/* Indicateur d'activité IA */}
      <Box
        sx={{
          position: 'fixed',
          top: 20,
          right: 20,
          zIndex: 999,
          bgcolor: 'success.main',
          color: 'white',
          px: 2,
          py: 1,
          borderRadius: 2,
          boxShadow: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}
      >
        <AIIcon sx={{ fontSize: 16, animation: 'pulse 2s infinite' }} />
        <Typography variant="caption">
          IA Active
        </Typography>
      </Box>

      {/* Styles pour l'animation */}
      <style jsx>{`
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.5; }
          100% { opacity: 1; }
        }
      `}</style>
    </Box>
  );
};

export default AppWithAI;